import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import DataGridUtils from "../../../../packages/data-grid/core/services/utils";
import * as $ from "jquery";
import classnames from "classnames";
import Draggable from "react-draggable";
import DateUtils from "../../../../core/services/date-utils";

// the default size of the switch
const DEFAULT_SIZE = 6;

const DraggableSwitch = ({
                             onChange,
                             size = DEFAULT_SIZE,
                             className = '',
                             disabled = false,
                             checked: checkedProp = false,
                             id: idProp,
                             activeTitle = 'ON',
                             inActiveTitle = 'OFF'
                             // animationDelay = DefaultTableRowAnimationProps.timeout,
                         }) => {

    const id = useRef(idProp || DataGridUtils.createUUId(true));
    const containerId = useRef(DataGridUtils.createUUId(true));
    const [checked, setChecked] = useState(checkedProp ?? false);
    const [switchSize, setSwitchSize] = useState(size);
    const [timer, setTimer] = useState(0);
    const draggableRef = useRef();

    /** @type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();

    /**
     * As soon as the component mounts:
     * sets the switch's size if it is not in a valid range
     */
    useLayoutEffect(() => {
        if (!(size >= 1 && size <= 10) || size === undefined)
            setSwitchSize(DEFAULT_SIZE);
    }, []);

    /**
     * Listens to the checked state
     * Call the onChange method provided from props
     */
    useEffect(() => {
        if (!id.current)
            return;
        moveSwitch(checked);
        if (checked === checkedProp)
            return;
        const prev = !checked;
        if (onChange)
            onChange(checked, () => {
                setChecked(prev);
            });
    }, [checked, id.current]);

    /**
     * Listens to the checked state
     * Call the onChange method provided from props
     */
    useEffect(() => {
        if (checked === checkedProp) return
        setChecked(checkedProp ?? false);
    }, [checkedProp]);

    /**
     * Handles the onClick event for the switch
     * @param {Event} e
     * @param {boolean} force
     */
    const onClick = (e, force = false) => {
        if (disabled && !force) return;
        setChecked(prevState => !prevState);
        e.stopPropagation();
        e.preventDefault();
    }

    /**
     * Sets the timer when the drag functionality starts
     */
    const onDragStart = () => {
        setTimer(DateUtils.localNow().valueOf());
    }

    /**
     * Determines whether the user has dragged to switch or clicked on it.
     * @return {boolean}
     */
    const determineIfUserDraggedTheSwitch = () => {
        const startTime = timer;
        const endTime = DateUtils.localNow().valueOf();
        // Checking if the user clicked on the switch handle or dragged it
        return endTime - startTime <= 300;
    }

    /**
     * Computes the translateX to checked section
     * @return {number}
     */
    const computeTransfer = () => {
        const switchElm = document.querySelector(`#${id.current}`);

        if (!containerRef.current)
            return 0;

        // Getting the styles of the wrapping component
        const containerComputedStyle = getComputedStyle(containerRef.current)
        // Getting the container width including padding, excluding margin and border
        const containerWidth = containerRef.current?.clientWidth ?? 0;
        const pureContainerWidth = containerWidth - parseFloat(containerComputedStyle?.paddingRight)

        return (pureContainerWidth ?? 0) - (switchElm?.clientWidth ?? 0) - 2;
    }

    /**
     * Moves the switch thumb component in the container.
     * @param {boolean} checked whether to move to switch to on or off state.
     */
    const moveSwitch = (checked) => {
        const switchElm = document.querySelector(`#${id.current}`);
        if (!checked) {
            switchElm.style.transform = `translateX(-2px)`
        } else {

            switchElm.style.transform = `translateX(${computeTransfer()}px)`;
        }
    }

    /**
     * Handles and moves the switch handle using the current position
     * to the proper position
     * @param {DraggableEvent}  e
     * @param {DraggableData}   data
     */
    const onDragStop = (e, data) => {
        if (determineIfUserDraggedTheSwitch()) {
            draggableRef.current?.classList?.remove('no-transition');
            return requestAnimationFrame(() => onClick(e))
        }

        const switchEle = $(`#${id.current}`);
        const {left: switchLeft} = switchEle.position();
        const switchWidth = switchEle.outerWidth();
        const containerWidth = $(`#${containerId.current}`).outerWidth();
        const switchInOffArea = (switchLeft + (switchWidth / 2)) < (containerWidth / 2);
        const switchInOnArea = (switchLeft + (switchWidth / 2)) > (containerWidth / 2);
        let _checked = checked;
        if (switchInOffArea && checked) {
            _checked = false;
        }
        if (switchInOnArea && !checked) {
            _checked = true;
        }
        if (checked !== _checked) {
            setChecked(_checked);
        } else {
            moveSwitch(_checked);
        }
    }

    return (
        <div
            ref={containerRef}
            className={classnames(`draggable-switch-wrapper`, className, {disabled, checked})}
            id={containerId.current}
            style={{
                '--switch_size': switchSize,
            }}
            onClick={(e) => e.preventDefault() || e.stopPropagation()}
            onMouseDown={e => e.preventDefault() || e.stopPropagation()}
            onMouseUp={e => e.preventDefault() || e.stopPropagation()}
            onTouchStart={e => e.preventDefault() || e.stopPropagation()}
            onTouchEnd={e => e.preventDefault() || e.stopPropagation()}
        >
            <div
                className={classnames(`w-100 switch-text px-1 pr-2 text-left`, {'show': checked,})}
                onClick={onClick}>
                {activeTitle}
            </div>
            <div
                className={classnames(`w-100 switch-text px-1 pr-2 text-right`, {'show': !checked,})}
                onClick={onClick}>
                {inActiveTitle}
            </div>
            <Draggable
                axis='x'
                bounds='parent'
                scale={1}
                disabled={disabled}
                allowAnyClick={false}
                onStart={onDragStart}
                onStop={onDragStop}
                ref={draggableRef}
                defaultPosition={{x: -2, y: 0}}
                position={{
                    x: checked
                        ? computeTransfer()
                        : -2,
                    y: 0,
                }}
                defaultClassNameDragging={'no-transition'}
            >
                <div
                    className={classnames(`switch`)}
                    id={id.current}
                />
            </Draggable>
        </div>
    )
}

export default DraggableSwitch;
